import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import CollectiveAgreementForm from "../../components/Shared/CollectiveAgreementForm/CollectiveAgreementForm";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { CreateConsultantCollectiveAgreementsBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import {
	CreateCollectiveAgreementRequest,
	useCreateCollectiveAgreement,
} from "../../networking/collective-agreement.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { CollectiveAgreementApiSchema } from "../../schemas/collectiveAgreement.schema";

const CollectiveAgreementCreatePage: React.FC = () => {
	const { data: organisation } = useOrganisation();
	const createCollectiveAgreement = useCreateCollectiveAgreement();

	const { t } = useTranslation();
	const navigate = useNavigate();

	const form = useForm<CreateCollectiveAgreementRequest>({
		mode: "onBlur",
		resolver: zodResolver(CollectiveAgreementApiSchema),
		defaultValues: {
			employmentType: "fixedTerm",
			vacationSalaryPercent: 12.5,
			salaryStatistics: {
				wageType: "",
				agreementCode: "",
				federalNumber: "",
				occupationCode: "",
				personnelCategory: "",
				unionNumber: "",
				workTimeCode: "",
				workplaceNumberScb: "",
				workplaceNumberSn: "1",
			},
			obCompensation: [],
			guaranteedHoursByMonth: {
				1: 0,
				2: 0,
				3: 0,
				4: 0,
				5: 0,
				6: 0,
				7: 0,
				8: 0,
				9: 0,
				10: 0,
				11: 0,
				12: 0,
			},
		},
	});

	const { setValue } = form;

	const handleSubmit = async (formData: CreateCollectiveAgreementRequest) => {
		try {
			await createCollectiveAgreement.mutateAsync({
				body: formData,
			});

			navigate(routes.AGREEMENT);
		} catch (error) {
			// Error handling is done in the mutation hook
			console.error("Failed to create collective agreement:", error);
		}
	};

	useEffect(() => {
		if (organisation) {
			const defaultPool = organisation?.pools?.find((p) => p.isDefault);

			if (defaultPool) {
				// TODO: Set default values if needed
				// setValue("someField", defaultPool.key);
			}
		}
	}, [organisation, setValue]);

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={CreateConsultantCollectiveAgreementsBreadcrumbs} />
			<Flex margin="2rem auto 0 auto" direction="column">
				{!createCollectiveAgreement.isLoading ? (
					<CollectiveAgreementForm
						form={form}
						onSubmit={handleSubmit}
						isLoading={createCollectiveAgreement.isLoading}
						submitButtonText="Skapa kollektivavtal"
					/>
				) : (
					<LoadingIndicator />
				)}
			</Flex>
		</div>
	);
};

export default CollectiveAgreementCreatePage;
